import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, MessageCircle, Search, Wallet, History } from "lucide-react";
import { toast } from "sonner";
import CustomerBalance from "@/pages/CustomerBalance";
import CustomerHistory from "@/pages/CustomerHistory";

const blank = { name: "", address: "", phone: "", cpf: "", notes: "" };

function whatsappLink(phone) {
  const clean = (phone || "").replace(/\D/g, "");
  return `https://wa.me/${clean.length >= 11 ? "55" + clean : clean}`;
}

export default function Clientes() {
  const [list, setList] = useState([]);
  const [store, setStore] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [balanceFor, setBalanceFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [onlyPending, setOnlyPending] = useState(false);

  // Permite vir do Crediário (/clientes?pending=1) já com a aba "Pendentes"
  // ativa, ou abrir direto o saldo de um cliente específico (?open=<id>).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pending") === "1") setOnlyPending(true);
    const openId = params.get("open");
    if (openId) {
      api.get(`/customers/${openId}`).then(r => setBalanceFor(r.data)).catch(()=>{});
    }
  }, []);

  const load = async () => {
    const r = await api.get("/customers", { params: { q: q || undefined }, _cache: false });
    setList(r.data);
  };
  useEffect(() => {
    load();
    api.get("/store").then(r => setStore(r.data)).catch(()=>{});
  }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...blank, ...c }); setEditingId(c.id); setOpen(true); };

  const submit = async () => {
    if (!form.name || !form.phone) return toast.error("Nome e telefone obrigatórios");
    if (editingId) await api.put(`/customers/${editingId}`, form);
    else await api.post("/customers", form);
    setOpen(false); toast.success("Cliente salvo"); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir cliente?")) return;
    await api.delete(`/customers/${id}`); load();
  };

  return (
    <div className="space-y-8" data-testid="clientes-page">
      <div className="flex flex-wrap gap-4 justify-between items-end">
        <div>
          <p className="label-tiny mb-2">Cadastros</p>
          <h1 className="font-display text-4xl tracking-tight">Clientes</h1>
        </div>
        <Button onClick={openNew} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-customer-button"><Plus size={16} className="mr-1"/> Novo cliente</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nome…" className="pl-9" data-testid="customer-search-input"/>
        </div>
        <Button
          type="button"
          variant={onlyPending ? "default" : "outline"}
          onClick={()=>setOnlyPending(v => !v)}
          className={onlyPending ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-700 border-red-200 hover:bg-red-50"}
          data-testid="filter-pending-btn"
          title="Mostrar apenas clientes com saldo devedor"
        >
          <Wallet size={14} className="mr-1"/>
          {onlyPending ? "Exibindo pendentes" : "Pendentes"}
          {onlyPending && (
            <span className="ml-2 text-[11px] bg-white/20 px-1.5 py-0.5 rounded">
              {list.filter(c => Number(c.balance || 0) < 0).length}
            </span>
          )}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(onlyPending ? list.filter(c => Number(c.balance || 0) < 0) : list).map(c => {
          const bal = Number(c.balance || 0);
          const balColor = bal < 0 ? "text-red-600" : bal > 0 ? "text-emerald-600" : "text-neutral-700";
          return (
            <Card key={c.id} className="p-5 border-[#E5E5E5] shadow-none hover:-translate-y-1 transition" data-testid={`customer-card-${c.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500">{c.phone}</div>
                </div>
                <div className="flex gap-1">
                  <a href={whatsappLink(c.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-green-50 text-green-700 hover:bg-green-100" data-testid={`whatsapp-${c.id}`}>
                    <MessageCircle size={14}/>
                  </a>
                  <Button size="sm" variant="ghost" onClick={()=>openEdit(c)} data-testid={`edit-customer-${c.id}`}><Edit size={12}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>remove(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>

              {/* Saldo */}
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="label-tiny">Saldo</div>
                  <div className={`text-lg font-display mt-0.5 ${balColor}`} data-testid={`balance-${c.id}`}>{brl(bal)}</div>
                </div>
                <div className="text-right text-[10px] text-neutral-500">
                  <div>{c.pending_count || 0} em aberto</div>
                  <div>{c.finalized_count || 0} finalizados</div>
                </div>
              </div>

              {/* Botões saldo + histórico */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={()=>setBalanceFor(c)} className="text-xs" data-testid={`adjust-balance-btn-${c.id}`}>
                  <Wallet size={12} className="mr-1"/> Ajustar saldo
                </Button>
                <Button size="sm" variant="outline" onClick={()=>setHistoryFor(c)} className="text-xs" data-testid={`history-btn-${c.id}`}>
                  <History size={12} className="mr-1"/> Histórico
                </Button>
              </div>

              {c.address && <div className="text-[11px] text-neutral-500 mt-3 truncate">{c.address}</div>}
            </Card>
          );
        })}
        {list.length === 0 && <div className="col-span-full text-sm text-neutral-500 py-12 text-center">Nenhum cliente cadastrado.</div>}
        {list.length > 0 && onlyPending && list.filter(c => Number(c.balance || 0) < 0).length === 0 && (
          <div className="col-span-full text-sm text-neutral-500 py-12 text-center" data-testid="no-pending-customers">
            Nenhum cliente com saldo devedor.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={e=>update("name", e.target.value)} data-testid="customer-name-input"/></div>
            <div><Label>Telefone *</Label><Input value={form.phone} onChange={e=>update("phone", e.target.value)} placeholder="(00) 00000-0000" data-testid="customer-phone-input"/></div>
            <div><Label>Endereço</Label><Input value={form.address} onChange={e=>update("address", e.target.value)}/></div>
            <div><Label>CPF</Label><Input value={form.cpf} onChange={e=>update("cpf", e.target.value)}/></div>
            <div><Label>Observações</Label><Textarea value={form.notes} onChange={e=>update("notes", e.target.value)} rows={3}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-customer-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {balanceFor && (
        <CustomerBalance customer={balanceFor} store={store}
          onClose={()=>{ setBalanceFor(null); load(); }}
          onChanged={load}/>
      )}
      {historyFor && (
        <CustomerHistory customer={historyFor} store={store} onClose={()=>setHistoryFor(null)}/>
      )}
    </div>
  );
}
